import React, { useMemo } from "react";
import { Button, GridItem } from "@chakra-ui/react";
import { format, parse } from "date-fns";
import { Form, Grid, Modal, NumberInput, Select } from "components";
import { generateCaseTimeInterval, useCurrentCase, useToast } from "duck";
import { AnestheticGas, Vital } from "requests";
import {
  FormValues,
  generateDefaultValues,
  getValidation,
  useCreateVitalsAndOxygen
} from "./duck";

interface CreateFiveMinModalProps {
  onClose(): void;
}

const CreateFiveMinModal: React.FC<CreateFiveMinModalProps> = React.memo(
  ({ onClose }) => {
    const toast = useToast();

    const { createdAt, procedures, anestheticGases, vitals } = useCurrentCase(
      medicalCase => ({
        createdAt: medicalCase.data.createdAt,
        procedures: medicalCase.data.client?.patient?.patientProcedures,
        anestheticGases: medicalCase.data.client?.patient?.anestheticGases,
        vitals: medicalCase.data.client?.patient?.vitals
      })
    );

    const anesthesia = procedures?.find(
      procedure => procedure.name === "Anesthesia"
    );
    const timeIntervals = generateCaseTimeInterval(
      anesthesia?.startTime ?? createdAt,
      5
    );

    const caseCreationDate = new Date(createdAt);
    const timeOptions = timeIntervals.map(el => {
      const timeVal = parse(el, "h:mm a", caseCreationDate);
      const newLabel = format(timeVal, "h:mm aaaa");
      return { value: `${timeVal}`, label: newLabel };
    });

    const onSubmit = useCreateVitalsAndOxygen();

    const defaultValues = generateDefaultValues(
      anestheticGases as AnestheticGas[],
      vitals as Vital[],
      caseCreationDate
    );

    const timeTitle = useMemo(
      () => format(new Date(defaultValues.timeMonitoring), "h:mm aaaa"),
      []
    );

    return (
      <>
        <Modal title={timeTitle} onClose={onClose} isOpen size="xl">
          <Form<FormValues>
            {...getValidation()}
            defaultValues={defaultValues}
            onSubmit={values =>
              onSubmit(values)?.then(() => {
                toast({
                  status: "success",
                  description: `Anesthetic Gas and Oxygen and Vitals for ${format(
                    values.timeMonitoring as Date,
                    "h:mm aaaa"
                  )} are added successfully.`
                });
                onClose();
              })
            }
          >
            <Grid templateColumns={{ lg: "repeat(2, 1fr)" }} py={6}>
              <GridItem>
                <Select
                  name="timeMonitoring"
                  label="Time"
                  placeholder="Select time"
                  isDisabled
                  options={timeOptions}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="inhalantAnestheticAgent"
                  label="Inhalant Anesthetic Agent (%)"
                  placeholder="%"
                  min={0}
                  getValueLabel={value => `${value} %`}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="oxygen"
                  label="Oxygen L/min"
                  placeholder="L/min"
                  min={0}
                  getValueLabel={value => `${value} L/min`}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="heartRate"
                  label="Heart Rate (HR)"
                  placeholder="Heart Rate"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="respiratoryRate"
                  label="Respiratory Rate (RR)"
                  placeholder="Respiratory Rate"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="pulseOximetry"
                  label="Pulse Oximetry (SpO2)"
                  placeholder="Pulse Oximetry"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="endTidal"
                  label="End-tidal CO2"
                  placeholder="End-tidal CO2"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="systolicBloodPressure"
                  label="Systolic Blood Pressure (SBP)"
                  placeholder="Systolic Blood Pressure"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="meanBloodPressure"
                  label="Mean Blood Pressure (MBP)"
                  placeholder="Mean Blood Pressure"
                  min={0}
                />
              </GridItem>
              <GridItem>
                <NumberInput
                  name="diastolicBloodPressure"
                  label="Diastolic Blood Pressure (DBP)"
                  placeholder="Diastolic Blood Pressure"
                  min={0}
                />
              </GridItem>
            </Grid>
            <Form.Controls submitText="Save">
              <Button variant="outline" colorScheme="gray" onClick={onClose}>
                Cancel
              </Button>
            </Form.Controls>
          </Form>
        </Modal>
      </>
    );
  }
);

export default CreateFiveMinModal;
